import React, { forwardRef, ReactNode, Ref, useCallback } from 'react';
import { LiveSearch, LiveSearchProps } from '../form';
import { icModel } from 'generated-ts-common';
import { BlankState, NameCircle } from '../basic';
import { HBox, VBox } from '../layout';
import { intl } from '../../lib';

type HealthCareProvider = icModel.saf.infrastructure.model.hpd.view.DPOHealthCareProvider;

/**
 * Props for a {@link HPDLiveSearch} component.
 * @author agd
 */
export type HPDLiveSearchProps = Omit<
  LiveSearchProps<HealthCareProvider>,
  'label' | 'renderEmptyItems' | 'itemToKey' | 'itemToString' | 'menuClassName'
>;

/**
 * Function that renders each item of the result list.
 *
 * @param item a {@link HealthCareProvider}
 * @return the created {@link ReactNode}
 * @constructor
 */
function renderLiveSearchItem(item: HealthCareProvider): ReactNode {
  return (
    <HBox className="hpd-live-search-item">
      <NameCircle firstName={item.givenName} lastName={item.familyName} />
      <VBox className="hpd-live-search-item-content">
        <div className="displayName">{item.displayName}</div>
        <div className="specialty">
          {(item.speciality ?? item.type)?.join(', ')}
        </div>
      </VBox>
    </HBox>
  );
}

/**
 * {@link LiveSearch} component specifically for a search for {@link HealthCareProvider}s.
 *
 * @param properties a {@link HPDLiveSearchProps} object
 * @return the created {@link JSX.Element}.
 * @author agd
 * @constructor
 */
export const HPDLiveSearch = forwardRef(function HPDLiveSearch(
  {
    minQueryLength = 3,
    automaticallySelectSingleResult = true,
    renderItem = renderLiveSearchItem,
    ...props
  }: HPDLiveSearchProps,
  ref: Ref<HTMLInputElement>
): JSX.Element {
  const itemToString = useCallback(
    (item: HealthCareProvider) => item.displayName,
    []
  );
  const itemToKey = useCallback(
    (item: HealthCareProvider) => item.identifier,
    []
  );
  const renderEmptyItem = useCallback(
    () => (
      <BlankState
        iconName="person"
        description={intl('common.search.noentry')}
      />
    ),
    []
  );

  return (
    <LiveSearch<HealthCareProvider>
      {...props}
      ref={ref}
      menuClassName="ehs-hpd-live-search-menu"
      itemToKey={itemToKey}
      itemToString={itemToString}
      label={intl('view.button.search')}
      renderItem={renderItem}
      renderEmptyItems={renderEmptyItem}
      automaticallySelectSingleResult={automaticallySelectSingleResult}
      minQueryLength={minQueryLength}
    />
  );
});
